import type { JSX } from "react";

/**
 * A loading spinner component that mirrors react-loader-spinner's Oval
 * and centers it with accessibility support.
 *
 * @param props - Props forwarded to the Oval spinner component
 * @returns {JSX.Element} A centered Oval spinner
 */
interface CircleLoaderProps {
  visible?: boolean;
  height?: number | string;
  width?: number | string;
  color?: string;
  secondaryColor?: string;
  strokeWidth?: number;
  ariaLabel?: string;
  [key: string]: any;
}

/**
 * Converts a color to have 50% opacity
 * Supports hex colors (#rgb, #rrggbb) and CSS color names
 */
const applyOpacity = (color: string): string => {
  // If it's a hex color, convert to rgba with 50% opacity
  if (color.startsWith("#")) {
    // Remove the # symbol
    const hex = color.slice(1);

    // Handle 3-digit hex colors (#rgb)
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return `rgba(${r}, ${g}, ${b}, 0.5)`;
    }

    // Handle 6-digit hex colors (#rrggbb)
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.5)`;
    }
  }

  // For CSS color names or other formats, use CSS color-mix for opacity
  return `color-mix(in srgb, ${color} 50%, transparent)`;
};

const CircleLoader = ({
  visible = true,
  height = 32,
  width = 32,
  color = "#94A3B8",
  secondaryColor = "#94A3B8",
  strokeWidth = 4,
  ariaLabel = "loading...",
  ...rest
}: CircleLoaderProps): JSX.Element | null => {
  if (!visible) return null;

  // Apply 50% opacity to the secondary color
  const processedSecondaryColor = applyOpacity(secondaryColor);

  return (
    <div className="flex items-center justify-center">
      <div
        role="status"
        aria-label={ariaLabel}
        className="inline-block animate-spin"
        style={{
          height,
          width,
          borderWidth: strokeWidth,
          borderStyle: "solid",
          borderColor: processedSecondaryColor,
          borderTopColor: color,
          borderRadius: "50%",
        }}
        {...rest}
      >
        <span className="absolute! -m-px! h-px! w-px! overflow-hidden! whitespace-nowrap! border-0! p-0! [clip:rect(0,0,0,0)!]">
          Loading...
        </span>
      </div>
    </div>
  );
};

export default CircleLoader;
